import { Component, Injector, OnInit } from '@angular/core';
import { GoogleTagManagerService } from '@app/main/cablepro/google-tag-manager.service';
import { LoaderService } from '@app/main/cablepro/loader.service';
import { Title } from '@node_modules/@angular/platform-browser';
import { Router, ActivatedRoute, NavigationEnd } from '@node_modules/@angular/router';
import { filter, finalize, map } from '@node_modules/rxjs/operators';
import { AppComponentBase } from '@shared/common/app-component-base';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { MomentHelper } from '@shared/helpers/MomentHelper';
import { CompanyAccountService } from '@shared/service-proxies/company-account.service';
import { AccountServiceProxy, CommonLookupServiceProxy, SendEmailActivationLinkInput, SubscriptionServiceProxy, UserLoginInfoDto } from '@shared/service-proxies/service-proxies';
import { RegionalStandardService } from './services/regional-standard.service';

@Component({
  selector: 'app-webfacing',
  templateUrl: './webfacing.component.html',
  styleUrls: ['./webfacing.component.css'],
})
export class WebfacingComponent extends AppComponentBase implements OnInit {

  public user!: UserLoginInfoDto | undefined;
  public calculationStandard: number = 0;
  public isAdminUser: boolean = false;
  public unRegisteredUser: boolean = false;
  public hasPlanExpired: boolean = false;
  private planDueDays: number = 0;
  hideHeader = false;
  public model: SendEmailActivationLinkInput = new SendEmailActivationLinkInput();
  public saving = false;
  constructor(injector: Injector, private readonly _router: Router,
    private readonly gtmService: GoogleTagManagerService,
    private readonly titleService: Title,
    private readonly route: ActivatedRoute,
    private readonly _commonLookupServiceProxy: CommonLookupServiceProxy,
    public readonly _appSessionService: AppSessionService,
    private readonly _companyAccountService: CompanyAccountService,
    public readonly loaderService: LoaderService,
    private readonly _regionalStandardService: RegionalStandardService,
    private readonly _subscriptionService: SubscriptionServiceProxy,
    private readonly _accountService: AccountServiceProxy) {
    super(injector);
    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.hideHeader = this._router.url.includes('/maintenance');
      });
  }

  public blockUI: boolean = false;
  ngOnInit(): void {

    this.loaderService.blockUI$.subscribe(block => {
      if (block) {
        this.blockUI = true;
        // hide, disable, or stop rendering
      }
    });
    // Handle initial load
    this.setTitle(this.route);

    // CPW-5547: resolve the visitor's regional standard once per session. Runs on every calculator
    // page and the Index page - not just Cable Sizing - so the answer is already cached by the time
    // the user reaches a Cable Sizing page, wherever they landed first.
    this.maybeDetectRegion(this._router.url);
    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => this.maybeDetectRegion(event.url));

    // Handle title updates on navigation
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.route),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        map(route => route.snapshot.data['title'])
      )
      .subscribe(title => {
        if (title) {
          this.titleService.setTitle(`${title}`);
        }
      });

    this.user = this._appSessionService.user;
    if (!(this._router.url.indexOf('/pricing') > -1)) {
      if (this.user && !this.user.isWebFacingUser) {
        this.isAdminUser = (this.user.id === 1 || this.user.id === 2);
        this.hasPlanExpired = this._appSessionService.user.premiumUser && MomentHelper.IsSubscriptionDateExpired(this.user.expirationDate);
        this.planDueDays = MomentHelper.GetSubscriptionDueDays(this.user.expirationDate);
        this.unRegisteredUser = false;

        if (this.user.userStripeCustomerId === null || this.user.userStripeCustomerId === undefined) {
          this.unRegisteredUser = true;
        }
        else if (this.user.userStripeCustomerId.trim().length == 0 || this.user.userStripeCustomerId.trim() == '0') {
          this.unRegisteredUser = true;
        }
        this.calculationStandard = this.user.userSetting?.standards_CableSizing || 0;
        if (this.user.periodEndDate != null && this.user.periodEndDate != undefined
          && this.user.periodEndDate == "Inactive") {
          this.loaderService.setBlockUI(true);
          this.handleUnlicencesOrExpiredFlow(this.user, this.calculationStandard, this._companyAccountService, this._router);
        }
        else {
          if (this.user.periodEndDate != null && this.user.periodEndDate != undefined
            && this.user.periodEndDate != "succeeded") {
            this.loaderService.setBlockUI(true);
            this.handleFailedOrUnpaidFlow(this.user, this.loaderService, this._subscriptionService, this._companyAccountService, this._router);
          }
        }

        if (!this.isAdminUser) {
          if (this.unRegisteredUser && (this.hasPlanExpired || (this.planDueDays >= -7 && this.planDueDays <= 0))) {
            if (this._appSessionService.user.premiumUser && this.hasPlanExpired) {
              this.loaderService.setBlockUI(true);
              this.handleUnlicencesOrExpiredFlow(this.user, this.calculationStandard, this._companyAccountService, this._router);
            }
          }
        }
      }
    }

    this.promptEmailVerificationIfRequired();
  }

  private promptEmailVerificationIfRequired(): void {
    const user = this._appSessionService.user;
    if (!user) {
      return;
    }

    if (user.isWebFacingUser) {
      return;
    }

    if (localStorage.getItem('IsEmailConfirmationRequired') !== 'true') {
      return;
    }

    this._modalPopupService.confirm(
      'Email Verification Required',
      'Your email address has not been verified yet.</br> Please check your inbox and click the verification link to activate your account.</br></br> If you didn’t receive the email, click <b>Resend Email</b> to get a new link.',
      (value) => {
        if (value) {
          this.model.emailAddress = user.emailAddress;
          this.saving = true;
          this._accountService.sendEmailActivationLink(this.model)
            .pipe(finalize(() => { this.saving = false; }))
            .subscribe(() => {
              this.message.success(
                'A verification link has been sent to your email address. Please check your inbox.',
                'Verification Email Sent'
              ).done(() => {
                this.logout();
              });
            });
        } else {
          this.logout();
        }
      },
      'fa-times-circle text-danger',
      'Resend Email',
      'btn-primary',
      'Log Out'
    );
  }

  checkApiStatus() {
    this._commonLookupServiceProxy.ping().subscribe();
  }

  /**
   * Calculator pages and the Index page only. WebfacingComponent is also the shell for /pricing,
   * /maintenance and /checkoutsuccess, none of which are in scope - and /maintenance in particular
   * would mean calling an API we already know is unreachable.
   */
  private static readonly DETECTION_ROUTES = [
    '/cable-sizing-as', '/cable-sizing-bs', '/cable-sizing-nz', '/cable-sizing-iec', '/cable-sizing-nec',
    '/max-demand', '/cable-pulling', '/arc-flash', '/protection-coordination'
  ];

  private maybeDetectRegion(url: string): void {
    if (this.isDetectionEligibleRoute(url)) {
      this._regionalStandardService.ensureDetected(); // idempotent
    }
  }

  private isDetectionEligibleRoute(url: string): boolean {
    const path = (url || '/').split('?')[0].split('#')[0];
    if (path === '/' || path === '') {
      return true; // Index
    }
    return WebfacingComponent.DETECTION_ROUTES.some(route => path.startsWith(route));
  }

  ngAfterViewInit() {
    this.gtmService.loadGTMContainer();
    this.checkApiStatus();
    setInterval(() => {
      this.checkApiStatus();
    }, 60000); // Check every 60 seconds
  }

  private setTitle(route: ActivatedRoute): void {
    let currentRoute = route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    const title = currentRoute.snapshot.data['title'];
    if (title) {
      this.titleService.setTitle(`${title}`);
    }
  }

}
